/* ═══ PER-SEAL QR LINKS — the customer's own serial + link sheet ═══════════════
   Owner 2026-10-20: "if they add 20 serials it will require them to add 20
   links + 20 serials for each of the specific links that will be QR codes ...
   an extra $.10 per sale" / "It will be a template. They can download the CSV
   and then enter a serial slot, and then the next column would be the link" /
   "give instructions in one of the sheets" / "limits on the amount of text they
   can type and limits on the URL size" / "sanitized and scanned on re-upload".

   window.SVQL is the whole engine; assets/sv-builder.js only hooks it in (the
   'list' QR chip, the preview, the blockers, the cart line). Nothing here
   touches the network except the two pinned libraries, loaded on demand:
     ExcelJS 4.4.0  builds the .xlsx template (Instructions + Seals sheets)
     SheetJS 0.18.5 reads an uploaded .xlsx / .xls / .ods
   CSV / TSV / pasted rows are parsed here, by hand.

   THE RULES — identical in three places, change them together or not at all:
   this file, Label Farm core/shopify_client._seal_qr_list and the render
   sidecar Serial Seal/tools/render_seals.read_qr_list.
     serial  1-14 of A-Z 0-9 '-', starting and ending with a letter or digit
             (14 = the legibility cap of the serial slot); lowercase is
             printed as capitals and the review shows it; unique in the sheet
     link    a web address or an email, at most 60 characters AFTER
             https:// is added (60 = the own-link QR density cap: longer codes
             stop scanning at seal size); printable ASCII, no spaces, no
             user name before the host, no script / data / file schemes
     rows    exactly one per seal on the line; packs of 12; at most 2,400
   The sheet rides the cart line as `_qr_list` ('SERIAL<TAB>LINK' rows joined
   by '\n', normalised, in print order) + `_qr_list_n` + `_qr_list_sha`
   (first 16 hex of SHA-256 of the exact text) so Label Farm can tell a sheet
   that was altered after this file checked it. */
(function () {
  'use strict';
  var L = { SERIAL: 14, LINK: 60, MAX_ROWS: 2400, PACK: 12, FILE_BYTES: 2 * 1024 * 1024,
            PRICE: 0.10 };
  var EXCELJS = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js';
  var SHEETJS = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
  var SERIAL_RE = /^[A-Z0-9](?:[A-Z0-9-]{0,12}[A-Z0-9])?$/;
  var HOST_RE = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/;
  var EMAIL_RE = /^[^\s@\/]+@[^\s@\/,;:]+\.[A-Za-z]{2,}$/;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(n) { return '$' + (Math.round(n * 100) / 100).toFixed(2); }
  function fmt(n) { return Number(n).toLocaleString('en-US'); }

  /* ── sanitise one cell ── zero-width and bidi controls are stripped (they
     are invisible in a spreadsheet and would print or break a link), other
     control characters and non-breaking spaces become spaces, then trim. What
     is left is CHECKED, never silently repaired - except capitals on a
     serial, which the review shows. */
  function clean(v) {
    return String(v == null ? '' : v)
      .replace(/[​-‏‪-‮⁠-⁤﻿]/g, '')
      .replace(/[\u0000-\u001F\u007F ]/g, ' ')
      .trim();
  }

  function checkSerial(raw) {
    var v = clean(raw);
    if (!v) return { err: 'No serial' };
    if (/^-?\d+(\.\d+)?E[+-]?\d+$/i.test(v)) {
      return { err: 'Your spreadsheet turned this serial into a number (' + v + '). Format the ' +
                    'Serial column as Text and type it again' };
    }
    var up = v.toUpperCase();
    if (up.length > L.SERIAL) {
      return { err: 'Serial is ' + up.length + ' characters — the most is ' + L.SERIAL };
    }
    if (/\s/.test(up)) return { err: 'Serials can’t contain spaces' };
    if (/[^A-Z0-9-]/.test(up)) {
      return { err: 'Serials use only letters, numbers and hyphens (found “' +
                    up.replace(/[A-Z0-9-]/g, '').slice(0, 3) + '”)' };
    }
    if (!SERIAL_RE.test(up)) return { err: 'A serial must start and end with a letter or number' };
    return { value: up };
  }

  /* the same normalisation the single own link gets (sv-builder normUrl) */
  function normLink(u) {
    if (/^mailto:/i.test(u)) return 'mailto:' + u.slice(7);
    if (EMAIL_RE.test(u)) return 'mailto:' + u;
    if (!/^https?:\/\//i.test(u)) return 'https://' + u;
    return u.replace(/^https?/i, function (s) { return s.toLowerCase(); });
  }
  function checkLink(raw) {
    var v = clean(raw);
    if (!v) return { err: 'No link' };
    if (/\s/.test(v)) return { err: 'Links can’t contain spaces' };
    if (/^(javascript|data|vbscript|file|blob):/i.test(v)) {
      return { err: 'That kind of link can’t go on a seal — use a web address or an email' };
    }
    if (/[^\x21-\x7E]/.test(v)) {
      return { err: 'Use plain characters only — copy the link from your browser’s address bar' };
    }
    var sch = /^([a-z][a-z0-9+.-]*):\/\//i.exec(v);
    if (sch && !/^https?$/i.test(sch[1])) {
      return { err: 'Only web addresses (https://…) and email addresses can go on a seal' };
    }
    var u = normLink(v);
    if (/^mailto:/i.test(u)) {
      if (!EMAIL_RE.test(u.slice(7))) return { err: 'That email address isn’t complete' };
    } else {
      var m = /^https?:\/\/([^\/?#]*)/i.exec(u);
      var auth = m ? m[1] : '';
      if (auth.indexOf('@') >= 0) return { err: 'Remove the name and @ in front of the website' };
      var host = /:\d{1,5}$/.test(auth) ? auth.replace(/:\d{1,5}$/, '') : auth;
      if (!HOST_RE.test(host.toLowerCase())) {
        return { err: 'Not a full web address — like psacard.com/cert/12345678' };
      }
    }
    if (u.length > L.LINK) {
      return { err: 'Link is ' + u.length + ' characters' + (u !== v ? ' once https:// is added' : '') +
                    ' — the most a seal’s QR scans at is ' + L.LINK +
                    '. Use a shorter link (bit.ly or your own short link)' };
    }
    return { value: u };
  }

  /* ── reading ───────────────────────────────────────────────────────── */
  var _libs = {};
  function loadScript(src, ready) {
    if (ready()) return Promise.resolve();
    if (_libs[src]) return _libs[src];
    _libs[src] = new Promise(function (res, rej) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () { ready() ? res() : rej(new Error('library did not load')); };
      s.onerror = function () { delete _libs[src]; rej(new Error('library did not load')); };
      document.head.appendChild(s);
    });
    return _libs[src];
  }

  /* CSV / TSV / pasted text. The delimiter is the first line's: tab (a paste
     from a spreadsheet), else comma, else semicolon (European CSV). Quoted
     fields and doubled quotes are honoured, so a link with a comma survives
     a quoted CSV. */
  function parseDelimited(text) {
    text = String(text || '').replace(/^﻿/, '');
    var first = '';
    var lines = text.split(/\r\n|\n|\r/);
    for (var i = 0; i < lines.length; i++) if (lines[i].trim()) { first = lines[i]; break; }
    var d = first.indexOf('\t') >= 0 ? '\t'
          : (first.indexOf(',') >= 0 || first.indexOf(';') < 0) ? ',' : ';';
    var out = [], row = [], f = '', q = false, c;
    for (i = 0; i < text.length; i++) {
      c = text.charAt(i);
      if (q) {
        if (c === '"') {
          if (text.charAt(i + 1) === '"') { f += '"'; i++; } else q = false;
        } else f += c;
      } else if (c === '"' && f === '') {
        q = true;
      } else if (c === d) {
        row.push(f); f = '';
      } else if (c === '\n' || c === '\r') {
        if (c === '\r' && text.charAt(i + 1) === '\n') i++;
        row.push(f); out.push(row); row = []; f = '';
      } else f += c;
    }
    if (f !== '' || row.length) { row.push(f); out.push(row); }
    return out;
  }

  function parseWorkbook(buf) {
    return loadScript(SHEETJS, function () { return !!window.XLSX; }).then(function () {
      var wb = XLSX.read(new Uint8Array(buf), { type: 'array', cellDates: false, cellText: true });
      var names = wb.SheetNames || [];
      /* the template's data sheet is 'Seals'; otherwise the first sheet that
         is not the instructions and has something in it */
      var pick = null;
      names.forEach(function (n) { if (!pick && /^seals?$/i.test(String(n).trim())) pick = n; });
      if (!pick) {
        names.forEach(function (n) {
          if (pick || /instruction/i.test(n)) return;
          var ws0 = wb.Sheets[n];
          if (ws0 && ws0['!ref']) pick = n;
        });
      }
      if (!pick) return [];
      var ws = wb.Sheets[pick];
      var ref = ws['!ref'];
      if (!ref) return [];
      var rg = XLSX.utils.decode_range(ref);
      var rowsOut = [];
      for (var r = rg.s.r; r <= rg.e.r; r++) {
        var row = [];
        for (var c = rg.s.c; c <= rg.e.c; c++) {
          var cell = ws[XLSX.utils.encode_cell({ r: r, c: c })];
          if (!cell) { row.push(''); continue; }
          /* the DISPLAYED text (w) - what the customer sees in the cell - but
             a hyperlink cell whose label differs from its target keeps the
             visible text: we print what they can read and check */
          var v = cell.w != null ? cell.w : (cell.v != null ? String(cell.v) : '');
          if (cell.t === 'n' && /e\+/i.test(String(cell.w || ''))) v = String(cell.w);
          row.push(v);
        }
        rowsOut.push(row);
      }
      return rowsOut;
    });
  }

  /* ── checking a whole sheet ── returns the list object the builder keeps */
  var HEAD_SERIAL = /serial|^code$|^seal|^number$|^id$/i;
  var HEAD_LINK = /link|url|qr|web|address|destination/i;
  function check(grid, name, opts) {
    opts = opts || {};
    var res = { name: name || 'your sheet', rows: [], errs: [], n: 0, ok: false, text: '', sha: '',
                pick: 0, ack: false };
    grid = (grid || []).map(function (r) { return (r || []).map(function (c) { return String(c == null ? '' : c); }); });
    /* header: the first non-empty row, when it names a serial column and a
       link column. Otherwise column A is the serial and B the link. */
    var start = 0, cs = 0, cl = 1, width = 0, header = false;
    while (start < grid.length && !grid[start].some(function (c) { return clean(c); })) start++;
    if (start < grid.length) {
      var h = grid[start].map(clean);
      var si = -1, li = -1;
      h.forEach(function (t, i) {
        if (si < 0 && HEAD_SERIAL.test(t)) si = i;
        else if (li < 0 && HEAD_LINK.test(t)) li = i;
      });
      /* a header names the columns; a cell that IS a link (a dot or a slash
         in it) is data - 'SEAL-001 | https://mylink.com' must never be
         dropped as a header. The table path has no header at all. */
      if (!opts.noHeader && si >= 0 && li >= 0 && si !== li && !/[.\/]/.test(h[li])) {
        cs = si; cl = li; header = true; width = h.length; start++;
      }
    }
    var seen = {};
    for (var i = start; i < grid.length; i++) {
      var raw = grid[i];
      var sv = raw[cs] == null ? '' : raw[cs], lv = raw[cl] == null ? '' : raw[cl];
      /* A ROW WIDER THAN IT SHOULD BE is a link that was split at a comma
         (an unquoted CSV) - printing the first half would put a wrong link
         on a seal, so it is an error, never a guess. Extra NAMED columns in a
         headed sheet (notes, card names, the template's Seal #) are fine. */
      var extras = raw.filter(function (c, k) {
        return k !== cs && k !== cl && clean(c) && (!header || k >= width);
      });
      /* blank = nothing in the serial, the link or an unexpected column (the
         template's unfilled rows still carry their Seal # helper) */
      if (!clean(sv) && !clean(lv) && !extras.length) continue;
      var rowNo = i + 1;
      var r = { row: rowNo, serial: '', link: '', errs: [] };
      if (extras.length) {
        r.errs.push('This row has more than a serial and a link — if a link contains a comma, ' +
                    'put it in quotes or use the Excel template');
      }
      var s = checkSerial(sv), l = checkLink(lv);
      if (s.err) r.errs.push(s.err); else r.serial = s.value;
      if (l.err) r.errs.push(l.err); else r.link = l.value;
      r.rawSerial = clean(sv);
      r.rawLink = clean(lv);
      if (r.serial) {
        if (seen[r.serial]) r.errs.push('Serial ' + r.serial + ' is already on row ' + seen[r.serial]);
        else seen[r.serial] = rowNo;
      }
      res.rows.push(r);
    }
    res.n = res.rows.length;
    res.rows.forEach(function (r) {
      r.errs.forEach(function (e) {
        res.errs.push({ row: r.row, serial: r.serial || r.rawSerial, msg: e });
      });
    });
    if (!res.n) res.errs.push({ row: 0, serial: '', msg: 'No rows found — put each serial in column A and its link in column B' });
    if (res.n > L.MAX_ROWS) {
      res.errs.push({ row: 0, serial: '', msg: fmt(res.n) + ' rows — one design takes up to ' +
                      fmt(L.MAX_ROWS) + ' seals. Split your list into two designs.' });
    }
    res.ok = !res.errs.length;
    if (res.ok) {
      res.text = res.rows.map(function (r) { return r.serial + '\t' + r.link; }).join('\n');
    }
    return res;
  }

  function sha16(text) {
    try {
      var data = new TextEncoder().encode(text);
      return crypto.subtle.digest('SHA-256', data).then(function (buf) {
        return Array.prototype.map.call(new Uint8Array(buf), function (b) {
          return ('0' + b.toString(16)).slice(-2);
        }).join('').slice(0, 16);
      });
    } catch (e) { return Promise.reject(e); }
  }

  /* read a File (any of the accepted kinds) -> the checked list (Promise) */
  function readFile(file) {
    if (!file) return Promise.reject(new Error('No file'));
    if (file.size > L.FILE_BYTES) {
      return Promise.reject(new Error('That file is ' + (file.size / 1048576).toFixed(1) +
        ' MB — a sheet of serials and links is far smaller. Save just the Seals sheet as CSV and try again.'));
    }
    var nm = String(file.name || '').toLowerCase();
    var book = /\.(xlsx|xlsm|xls|ods)$/.test(nm);
    var text = /\.(csv|tsv|txt)$/.test(nm) || /^text\//.test(file.type || '');
    if (!book && !text) {
      return Promise.reject(new Error('Upload the template as .xlsx, or a .csv / .tsv file'));
    }
    return new Promise(function (res, rej) {
      var rd = new FileReader();
      rd.onerror = function () { rej(new Error('That file could not be read')); };
      rd.onload = function () { res(rd.result); };
      if (book) rd.readAsArrayBuffer(file); else rd.readAsText(file);
    }).then(function (data) {
      return book ? parseWorkbook(data) : parseDelimited(data);
    }).then(function (grid) { return finish(check(grid, file.name)); });
  }
  function readText(text) { return finish(check(parseDelimited(text), 'pasted rows')); }
  /* the typed table: [[serial, link], ...] by row index, kept in localStorage
     for 14 days so a refresh (or a phone switching apps) keeps the typing;
     cleared once the rows are in the cart (the builder calls clearDraft) */
  var DRAFT_KEY = 'ss_qrlist_table_v1', DRAFT_DAYS = 14;
  function loadDraft() {
    try {
      var d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      if (!d || !d.rows || Date.now() - (d.ts || 0) > DRAFT_DAYS * 864e5) return [];
      return d.rows.map(function (r) { return [String(r[0] || ''), String(r[1] || '')]; });
    } catch (_) { return []; }
  }
  function saveDraft(rows) {
    try {
      var last = rows.length;
      while (last > 0 && !rows[last - 1][0] && !rows[last - 1][1]) last--;
      if (!last) { localStorage.removeItem(DRAFT_KEY); return; }
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ts: Date.now(), rows: rows.slice(0, last) }));
    } catch (_) {}
  }
  /* the ACCEPTED sheet survives a refresh too (owner 2026-10-20: "save to
     local storage if they refresh or come back"): the exact text that passed,
     re-checked on the way back in (never trusted) and un-ticked again. The
     mode flag brings the builder back onto "A link per seal". All three go
     together once the rows are in the cart. */
  var LIST_KEY = 'ss_qrlist_list_v1', MODE_KEY = 'ss_qrlist_mode';
  function saveList(res) {
    try {
      if (res && res.ok && res.text) localStorage.setItem(LIST_KEY, JSON.stringify({ ts: Date.now(), text: res.text, name: res.name || 'your sheet' }));
    } catch (_) {}
  }
  function loadList() {
    try {
      var d = JSON.parse(localStorage.getItem(LIST_KEY) || 'null');
      if (!d || !d.text || Date.now() - (d.ts || 0) > DRAFT_DAYS * 864e5) return null;
      return d;
    } catch (_) { return null; }
  }
  function wantsList() {
    try { return localStorage.getItem(MODE_KEY) === '1' && !!(loadList() || loadDraft().length); } catch (_) { return false; }
  }
  function clearDraft() {
    try { localStorage.removeItem(DRAFT_KEY); localStorage.removeItem(LIST_KEY); localStorage.removeItem(MODE_KEY); } catch (_) {}
  }
  function finish(res) {
    if (!res.ok) return Promise.resolve(res);
    return sha16(res.text).then(function (h) { res.sha = h; return res; });
  }
  /* a sheet coming BACK (cart edit): the stored '_qr_list' text is re-checked
     from scratch, never trusted */
  function fromStored(text) {
    var grid = String(text || '').split('\n').map(function (l) { return l.split('\t'); });
    return finish(check(grid, 'your sheet'));
  }

  /* ── the template ──────────────────────────────────────────────────── */
  function saveBlob(blob, name) {
    var a = document.createElement('a');
    var url = URL.createObjectURL(blob);
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    setTimeout(function () { URL.revokeObjectURL(url); a.remove(); }, 1500);
  }
  var INSTR = function (n) {
    return [
      ['One QR link per seal — how to fill this in', 'title'],
      ['', ''],
      ['1.  Open the Seals tab (bottom of the window). Each row is ONE seal.', 'step'],
      ['     Column A = the serial printed on that seal.  Column B = the link its QR code opens.', 'text'],
      ['     This file has ' + fmt(n) + ' rows because you are ordering ' + fmt(n) + ' seals. Fill every row.', 'text'],
      ['', ''],
      ['2.  Serial (column A)', 'step'],
      ['     • 1 to 14 characters: letters A-Z, numbers 0-9 and hyphens ( - ). No spaces.', 'text'],
      ['     • Every serial must be different. Lowercase letters print as capitals.', 'text'],
      ['     • Examples:  NZ-0001    PSA-81234567    CARD7', 'text'],
      ['', ''],
      ['3.  Link (column B)', 'step'],
      ['     • The web address the QR opens — your site, a listing, a grading company’s certificate page — or an email address.', 'text'],
      ['     • Up to 60 characters, counting https:// at the start (we add it if you leave it off, and that counts too).', 'text'],
      ['     • No spaces. Too long? Use a short link (bit.ly, or a short link on your own site).', 'text'],
      ['     • Examples:  https://psacard.com/cert/81234567    mysite.com/c/7    hello@mysite.com', 'text'],
      ['', ''],
      ['4.  Save the file (keep it as .xlsx, or save the Seals sheet as CSV).', 'step'],
      ['     In the builder: QR code → A link per seal → Upload your sheet.', 'text'],
      ['', ''],
      ['5.  Check the review before you order.', 'step'],
      ['     We list every serial and link exactly as it will print. Tap a row to see it on the seal preview and scan it with your phone.', 'text'],
      ['     Any row we can’t print is listed with the reason — fix it in the sheet and upload it again.', 'text'],
      ['', ''],
      ['Good to know', 'step'],
      ['     • Serials and links print exactly as listed. A printed QR can’t be changed later — for a destination you can move, link to a page you control.', 'text'],
      ['     • $0.10 per seal, added as its own line in your cart.', 'text'],
      ['     • Seals come in sets of 12, so the number of rows must be a multiple of 12 (up to 2,400 per design).', 'text'],
      ['     • Not available on Duo seals — both halves of a Duo label share one QR code.', 'text']
    ];
  };
  function templateXlsx(n) {
    n = Math.max(L.PACK, Math.min(L.MAX_ROWS, parseInt(n, 10) || L.PACK));
    return loadScript(EXCELJS, function () { return !!window.ExcelJS; }).then(function () {
      var wb = new ExcelJS.Workbook();
      wb.creator = 'Serial Seal';
      wb.created = new Date();
      var ins = wb.addWorksheet('Instructions', { properties: { tabColor: { argb: 'FF7C4DFF' } },
                                                  views: [{ showGridLines: false }] });
      ins.getColumn(1).width = 130;
      INSTR(n).forEach(function (line, i) {
        var cell = ins.getCell(i + 1, 1);
        cell.value = line[0];
        cell.alignment = { wrapText: true, vertical: 'top' };
        if (line[1] === 'title') cell.font = { bold: true, size: 15, color: { argb: 'FF3B2A8F' } };
        else if (line[1] === 'step') cell.font = { bold: true, size: 12 };
        else cell.font = { size: 11 };
      });

      var ws = wb.addWorksheet('Seals', { views: [{ state: 'frozen', ySplit: 1 }] });
      ws.getColumn(1).width = 22;
      ws.getColumn(2).width = 66;
      ws.getColumn(3).width = 8;
      ws.getCell('A1').value = 'Serial';
      ws.getCell('B1').value = 'Link';
      ws.getCell('C1').value = 'Seal #';
      ['A1', 'B1', 'C1'].forEach(function (a) {
        var c = ws.getCell(a);
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3B2A8F' } };
      });
      var last = n + 1;
      for (var r = 2; r <= last; r++) {
        var a = ws.getCell('A' + r), b = ws.getCell('B' + r), c = ws.getCell('C' + r);
        /* TEXT cells: a serial typed as 0001 stays 0001 (a General cell turns
           it into the number 1), and a long number never becomes 1.2E+11 */
        a.numFmt = '@';
        b.numFmt = '@';
        c.value = r - 1;
        c.font = { color: { argb: 'FF8A8F9C' } };
        a.dataValidation = {
          type: 'custom', allowBlank: true, showErrorMessage: true, errorStyle: 'stop',
          formulae: ['AND(LEN(A' + r + ')<=' + L.SERIAL + ',ISERROR(FIND(" ",A' + r + ')),COUNTIF($A$2:$A$' + last + ',A' + r + ')=1)'],
          errorTitle: 'Serial',
          error: 'Up to ' + L.SERIAL + ' letters, numbers or hyphens, no spaces, and each serial only once.',
          showInputMessage: true, promptTitle: 'Serial',
          prompt: '1-' + L.SERIAL + ' characters: A-Z, 0-9 and -. Each one different.'
        };
        b.dataValidation = {
          type: 'textLength', operator: 'lessThanOrEqual', allowBlank: true,
          showErrorMessage: true, errorStyle: 'stop', formulae: [L.LINK],
          errorTitle: 'Link too long',
          error: 'Up to ' + L.LINK + ' characters counting https://. Use a shorter link (bit.ly or your own).',
          showInputMessage: true, promptTitle: 'Link',
          prompt: 'Web address or email, up to ' + L.LINK + ' characters counting https://'
        };
      }
      wb.views = [{ x: 0, y: 0, width: 12000, height: 9000, firstSheet: 0, activeTab: 0, visibility: 'visible' }];
      return wb.xlsx.writeBuffer();
    }).then(function (buf) {
      return new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    });
  }
  function templateCsv(n) {
    n = Math.max(L.PACK, Math.min(L.MAX_ROWS, parseInt(n, 10) || L.PACK));
    var out = ['Serial,Link'];
    for (var i = 0; i < n; i++) out.push(',');
    return new Blob(['﻿' + out.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' });
  }

  /* ── the panel ─────────────────────────────────────────────────────────
     api = { qty(): seals on this line, list(): current list or null,
             set(list|null), pick(i), minSeals(), maxSeals(), setQty(n) } */
  var CSS = [
    '.svql{margin-top:10px;font-size:13.5px;line-height:1.55;color:var(--ink,#e8ecf5)}',
    '.svql-lead{margin:0 0 10px;color:var(--muted,#98a1b8)}',
    '.svql-lead b{color:var(--ink,#e8ecf5)}',
    '.svql-steps{list-style:none;margin:0;padding:0;display:grid;gap:10px}',
    '.svql-step{border:1px solid var(--line,#232836);border-radius:10px;padding:10px 12px;background:rgba(122,168,255,.04)}',
    '.svql-step>b{display:block;margin-bottom:6px}',
    '.svql-row{display:flex;flex-wrap:wrap;gap:8px;align-items:center}',
    '.svql-btn{min-height:40px;padding:8px 14px;border-radius:9px;border:1px solid #3a4258;background:#1a1f2c;color:var(--ink,#e8ecf5);font:600 13.5px/1.2 inherit;cursor:pointer}',
    '.svql-btn:hover{border-color:#7aa8ff}',
    '.svql-btn:focus-visible,.svql-link:focus-visible,.svql-tr:focus-visible{outline:2px solid #9ad7ff;outline-offset:2px}',
    '.svql-btn.pri{background:#7c4dff;border-color:#7c4dff;color:#fff}',
    '.svql-btn[disabled]{opacity:.55;cursor:wait}',
    '.svql-link{background:none;border:0;padding:10px 6px;color:#9ad7ff;text-decoration:underline;font:inherit;cursor:pointer;min-height:44px;display:inline-flex;align-items:center}',
    '.svql-table{margin-top:10px}',
    '.svql-thint{font-size:12.5px;color:var(--muted,#98a1b8);margin:0 0 8px;line-height:1.5}',
    '.svql-tgrid{max-height:380px;overflow:auto;border:1px solid var(--line,#232836);border-radius:8px;background:#0f1219}',
    '.svql-trow{display:grid;grid-template-columns:36px minmax(96px,.8fr) minmax(0,2fr);gap:6px;align-items:center;padding:4px 8px;border-bottom:1px solid var(--line,#232836)}',
    '.svql-trow:last-child{border-bottom:0}',
    '.svql-trow.hd{position:sticky;top:0;z-index:1;background:#141824;font:700 11px/1.4 ui-sans-serif,system-ui,sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--muted,#98a1b8);padding:8px}',
    '.svql-trow .n{color:var(--muted,#98a1b8);font:12px/1 ui-monospace,Consolas,monospace;font-variant-numeric:tabular-nums}',
    '.svql-tin{width:100%;min-height:38px;padding:6px 8px;border-radius:7px;border:1px solid #2a3142;background:#12151d;color:var(--ink,#e8ecf5);font:13px/1.4 ui-monospace,Consolas,monospace;box-sizing:border-box}',
    '.svql-tin:focus{outline:2px solid #9ad7ff;outline-offset:1px;border-color:#9ad7ff}',
    '.svql-trow.bad .svql-tin{border-color:rgba(255,107,107,.75)}',
    '.svql-terr{grid-column:2/4;color:#ffb3b3;font-size:12px;line-height:1.4;padding:0 2px 4px}',
    '@media(max-width:520px){.svql-trow{grid-template-columns:30px minmax(0,1fr)}.svql-trow .n{grid-row:1/3}.svql-tin.tl{grid-column:2}.svql-terr{grid-column:2}}',
    '.svql-msg{margin-top:10px;padding:10px 12px;border-radius:10px;font-size:13px}',
    '.svql-msg.bad{border:1px solid rgba(255,107,107,.55);background:rgba(255,107,107,.08)}',
    '.svql-msg.good{border:1px solid rgba(80,220,150,.5);background:rgba(80,220,150,.07)}',
    '.svql-msg.warn{border:1px solid rgba(255,190,90,.55);background:rgba(255,190,90,.08)}',
    '.svql-msg ul{margin:6px 0 0;padding-left:18px}',
    '.svql-msg li{margin:2px 0;overflow-wrap:anywhere}',
    '.svql-tbl{margin-top:8px;max-height:260px;overflow:auto;border:1px solid var(--line,#232836);border-radius:8px}',
    '.svql-tr{display:grid;grid-template-columns:44px minmax(90px,.8fr) minmax(0,2fr);gap:8px;padding:6px 10px;border-bottom:1px solid var(--line,#232836);cursor:pointer;font:12.5px/1.4 ui-monospace,Consolas,monospace;font-variant-numeric:tabular-nums}',
    '.svql-tr:last-child{border-bottom:0}',
    '.svql-tr.hd{position:sticky;top:0;background:#141824;cursor:default;font:700 11px/1.4 ui-sans-serif,system-ui,sans-serif;letter-spacing:.06em;text-transform:uppercase;color:var(--muted,#98a1b8)}',
    '.svql-tr.on{background:rgba(124,77,255,.18)}',
    '.svql-tr span{overflow-wrap:anywhere;min-width:0}',
    '.svql-tr .n{color:var(--muted,#98a1b8)}',
    '.svql-ack{display:flex;gap:10px;align-items:flex-start;margin-top:10px;cursor:pointer}',
    '.svql-ack input{width:20px;height:20px;margin-top:1px;flex:0 0 auto}',
    '.svql-fee{margin-top:8px;color:var(--muted,#98a1b8)}',
    '@media(max-width:520px){.svql-tr{grid-template-columns:34px minmax(80px,1fr);}.svql-tr .l{grid-column:2}}'
  ].join('\n');
  function injectCss() {
    if (document.getElementById('svql-css')) return;
    var st = document.createElement('style');
    st.id = 'svql-css';
    st.textContent = CSS;
    document.head.appendChild(st);
  }

  function panel(host, api) {
    injectCss();
    var qty = api.qty();
    var list = api.list();
    var h = '<div class="svql">' +
      '<ol class="svql-steps">' +
      '<li class="svql-step"><b>1. Download the template</b>' +
        '<div class="svql-row"><button type="button" class="svql-btn" data-svql="xlsx">Excel template (.xlsx)</button>' +
        '<button type="button" class="svql-link" data-svql="csv">or CSV</button></div>' +
        '<div class="svql-fee"><span class="svql-rowsn">' + fmt(qty) + '</span> rows, one per seal, with the ' +
        'instructions on the first tab.</div></li>' +
      '<li class="svql-step"><b>2. Fill it in</b>Serial in column A (up to ' + L.SERIAL + ' letters, numbers or ' +
        'hyphens), the link it opens in column B (up to ' + L.LINK + ' characters).</li>' +
      '<li class="svql-step"><b>3. Upload it</b>' +
        '<div class="svql-row"><button type="button" class="svql-btn pri" data-svql="pick">' +
        (list ? 'Upload a new sheet' : 'Upload your sheet') + '</button>' +
        '<button type="button" class="svql-link" data-svql="table">or type them in</button></div>' +
        '<input type="file" hidden data-svql="file" accept=".xlsx,.xls,.ods,.csv,.tsv,.txt,' +
        'text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">' +
        '<div class="svql-table" hidden>' +
        '<p class="svql-thint">One row per seal. Type, or paste a column \u2014 or both columns \u2014 ' +
        'from your spreadsheet into a cell and it fills down from there. Enter moves to the next row.</p>' +
        '<div class="svql-tgrid"></div>' +
        '<div class="svql-row" style="margin-top:8px"><button type="button" class="svql-btn pri" data-svql="tcheck">Check these rows</button>' +
        '<button type="button" class="svql-link" data-svql="tclear">Clear the table</button></div></div></li>' +
      '</ol>' +
      '<div class="svql-out" role="status" aria-live="polite"></div></div>';
    host.innerHTML = h;
    var out = host.querySelector('.svql-out');
    var file = host.querySelector('[data-svql="file"]');
    try { localStorage.setItem(MODE_KEY, '1'); } catch (_) {}
    /* back after a refresh: the sheet that passed comes back, re-checked and
       un-ticked (they look again), unless a sheet is already in hand */
    if (!api.list()) {
      var _saved = loadList();
      if (_saved) {
        fromStored(_saved.text).then(function (r) {
          if (api.list()) return;
          r.name = _saved.name || 'your sheet';
          r.ack = false;
          api.set(r);
          paint();
        }).catch(function () {});
      }
    }

    function busy(btn, on, label) {
      if (!btn) return;
      if (on) { btn._t = btn.textContent; btn.textContent = label; btn.disabled = true; }
      else { btn.textContent = btn._t || btn.textContent; btn.disabled = false; }
    }
    function accept(res) {
      var cur = api.list();
      if (cur && res.ok && cur.sha === res.sha) res.ack = cur.ack;   /* same sheet again */
      if (res.ok) saveList(res);
      api.set(res);
      /* the sheet sets the quantity when it can be bought as-is */
      if (res.ok && res.n !== api.qty() && res.n % L.PACK === 0 &&
          res.n >= api.minSeals() && res.n <= Math.min(api.maxSeals(), L.MAX_ROWS)) {
        api.setQty(res.n);
      }
      paint();
    }
    function fail(e) {
      out.innerHTML = '<div class="svql-msg bad"><b>' + esc(e && e.message || 'That sheet could not be read') +
        '</b></div>';
    }
    host.querySelector('[data-svql="xlsx"]').onclick = function () {
      var b = this;
      busy(b, true, 'Building…');
      templateXlsx(api.qty()).then(function (blob) {
        saveBlob(blob, 'serial-seal-links-' + api.qty() + '.xlsx');
      }).catch(function () {
        fail(new Error('The Excel template couldn’t be built here — download the CSV instead'));
      }).then(function () { busy(b, false); });
    };
    host.querySelector('[data-svql="csv"]').onclick = function () {
      saveBlob(templateCsv(api.qty()), 'serial-seal-links-' + api.qty() + '.csv');
    };
    host.querySelector('[data-svql="pick"]').onclick = function () { file.value = ''; file.click(); };
    file.onchange = function () {
      var f = file.files && file.files[0];
      if (!f) return;
      var b = host.querySelector('[data-svql="pick"]');
      busy(b, true, 'Checking…');
      readFile(f).then(accept, fail).then(function () { busy(b, false); });
    };
    /* ── the input table ── one row per seal ordered, values by row index */
    var tableBox = host.querySelector('.svql-table');
    var grid = host.querySelector('.svql-tgrid');
    var tableBtn = host.querySelector('[data-svql="table"]');
    var draft = loadDraft();
    var _saveT = null;
    function rowsWanted() { return Math.max(1, Math.min(L.MAX_ROWS, api.qty() || 0)); }
    function cellAt(r, c) { return grid.querySelector('.svql-tin[data-r="' + r + '"][data-c="' + c + '"]'); }
    function renderTable() {
      var n = rowsWanted(), h = '<div class="svql-trow hd" aria-hidden="true"><span class="n">#</span><span>Serial</span><span>Link</span></div>';
      for (var i = 0; i < n; i++) {
        var d = draft[i] || ['', ''];
        h += '<div class="svql-trow" data-r="' + i + '"><span class="n">' + (i + 1) + '</span>' +
          '<input class="svql-tin ts" data-r="' + i + '" data-c="0" value="' + esc(d[0]) + '" ' +
          'aria-label="Serial, row ' + (i + 1) + '" maxlength="40" autocapitalize="characters" autocomplete="off" ' +
          'spellcheck="false" placeholder="' + (i === 0 ? 'NZ-0001' : '') + '">' +
          '<input class="svql-tin tl" data-r="' + i + '" data-c="1" value="' + esc(d[1]) + '" ' +
          'aria-label="Link, row ' + (i + 1) + '" maxlength="120" inputmode="url" autocomplete="off" ' +
          'autocapitalize="off" spellcheck="false" placeholder="' + (i === 0 ? 'psacard.com/cert/81234567' : '') + '"></div>';
      }
      grid.innerHTML = h;
    }
    function setCell(r, c, v) {
      while (draft.length <= r) draft.push(['', '']);
      draft[r][c] = v;
      var inp = cellAt(r, c);
      if (inp && inp.value !== v) inp.value = v;
    }
    function queueSave() {
      clearTimeout(_saveT);
      _saveT = setTimeout(function () { saveDraft(draft); }, 400);
    }
    function clearRowErrors() {
      grid.querySelectorAll('.svql-trow.bad').forEach(function (tr) { tr.classList.remove('bad'); });
      grid.querySelectorAll('.svql-terr').forEach(function (e) { e.remove(); });
    }
    grid.addEventListener('input', function (e) {
      var inp = e.target;
      if (!inp.classList || !inp.classList.contains('svql-tin')) return;
      setCell(+inp.getAttribute('data-r'), +inp.getAttribute('data-c'), inp.value);
      var tr = inp.closest('.svql-trow');
      if (tr && tr.classList.contains('bad')) {
        tr.classList.remove('bad');
        var er = tr.querySelector('.svql-terr'); if (er) er.remove();
      }
      queueSave();
    });
    /* spreadsheet paste: a block of cells fills down (and across) from the
       cell it was pasted into; a single value pastes like text */
    grid.addEventListener('paste', function (e) {
      var inp = e.target;
      if (!inp.classList || !inp.classList.contains('svql-tin')) return;
      var text = (e.clipboardData || window.clipboardData).getData('text') || '';
      if (!/[\r\n\t]/.test(text.trim())) return;          /* one value: let the browser paste it */
      e.preventDefault();
      var block = parseDelimited(text).filter(function (row) { return row.some(function (c) { return clean(c); }); });
      var r0 = +inp.getAttribute('data-r'), c0 = +inp.getAttribute('data-c'), n = rowsWanted();
      var over = 0;
      block.forEach(function (row, k) {
        var r = r0 + k;
        if (r >= n) { over++; return; }
        if (row.length >= 2) { setCell(r, 0, clean(row[0])); setCell(r, 1, clean(row[1])); }
        else setCell(r, c0, clean(row[0]));
      });
      queueSave();
      clearRowErrors();
      if (over) {
        out.innerHTML = '<div class="svql-msg warn">Pasted ' + fmt(block.length - over) + ' rows \u2014 the last ' +
          fmt(over) + ' didn\u2019t fit: you\u2019re ordering ' + fmt(n) + ' seals, so the table has ' + fmt(n) + ' rows.</div>';
      }
    });
    grid.addEventListener('keydown', function (e) {
      var inp = e.target;
      if (e.key !== 'Enter' || !inp.classList || !inp.classList.contains('svql-tin')) return;
      e.preventDefault();
      var nx = cellAt(+inp.getAttribute('data-r') + 1, +inp.getAttribute('data-c'));
      if (nx) { nx.focus(); nx.select(); }
    });
    function showTable(on) {
      tableBox.hidden = !on;
      tableBtn.textContent = on ? 'hide the table' : (draft.some(function (r) { return r[0] || r[1]; }) ? 'or edit the rows you typed' : 'or type them in');
      if (on) {
        renderTable();
        var first = cellAt(0, 0);
        if (first && !first.value) first.focus();
      }
    }
    tableBtn.onclick = function () { showTable(tableBox.hidden); };
    host.querySelector('[data-svql="tclear"]').onclick = function () {
      draft = []; clearDraft(); renderTable(); clearRowErrors();
      api.set(null); paint();
    };
    host.querySelector('[data-svql="tcheck"]').onclick = function () {
      var n = rowsWanted();
      var rows = [];
      for (var i = 0; i < n; i++) rows.push([(draft[i] || ['', ''])[0], (draft[i] || ['', ''])[1]]);
      if (!rows.some(function (r) { return clean(r[0]) || clean(r[1]); })) { (cellAt(0, 0) || {}).focus && cellAt(0, 0).focus(); return; }
      var res = check(rows, 'your table', { noHeader: true });
      clearRowErrors();
      /* an empty row inside the table is a missing seal - say so on the row */
      res.rows.forEach(function (r) { r.row = r.row; });
      var filled = res.rows.length;
      if (res.ok && filled < n) {
        res.ok = false;
        res.errs.push({ row: 0, serial: '', msg: 'Only ' + fmt(filled) + ' of ' + fmt(n) + ' rows are filled in \u2014 every seal needs its serial and link' });
      }
      res.errs.forEach(function (er) {
        if (!er.row) return;
        var tr = grid.querySelector('.svql-trow[data-r="' + (er.row - 1) + '"]');
        if (!tr) return;
        tr.classList.add('bad');
        var el2 = document.createElement('div');
        el2.className = 'svql-terr';
        el2.textContent = er.msg;
        tr.appendChild(el2);
      });
      /* rows the check skipped as blank, inside the range: mark them */
      if (filled < n) {
        var seen = {};
        res.rows.forEach(function (r) { seen[r.row] = true; });
        for (var k = 1; k <= n; k++) {
          if (seen[k]) continue;
          var tr2 = grid.querySelector('.svql-trow[data-r="' + (k - 1) + '"]');
          if (tr2 && !tr2.classList.contains('bad')) {
            tr2.classList.add('bad');
            var e3 = document.createElement('div'); e3.className = 'svql-terr'; e3.textContent = 'Empty row \u2014 this seal needs a serial and a link';
            tr2.appendChild(e3);
          }
        }
      }
      finish(res).then(function (r) {
        accept(r);
        if (r.ok) showTable(false);
        else {
          var bad = grid.querySelector('.svql-trow.bad .svql-tin');
          if (bad) bad.focus();
        }
      }, fail);
    };

    function paint() {
      var lst = api.list(), q = api.qty();
      var rn = host.querySelector('.svql-rowsn');
      if (rn) rn.textContent = fmt(q);
      if (!tableBox.hidden && grid.querySelectorAll('.svql-trow[data-r]').length !== rowsWanted()) renderTable();
      if (!lst) { out.innerHTML = ''; return; }
      var hh = '';
      if (!lst.ok) {
        var shown = lst.errs.slice(0, 40);
        hh += '<div class="svql-msg bad"><b>' + (lst.errs.length === 1 ? '1 problem' : fmt(lst.errs.length) + ' problems') +
          ' in ' + esc(lst.name) + '</b> — nothing goes in your cart until every row passes. ' +
          (lst.name === 'your table' ? 'Fix them in the table and check again:' : 'Fix these in the sheet and upload it again:') + '<ul>' +
          shown.map(function (e) {
            return '<li>' + (e.row ? 'Row ' + e.row + (e.serial ? ' (' + esc(e.serial) + ')' : '') + ': ' : '') +
              esc(e.msg) + '</li>';
          }).join('') +
          (lst.errs.length > shown.length ? '<li>…and ' + fmt(lst.errs.length - shown.length) + ' more</li>' : '') +
          '</ul></div>';
        out.innerHTML = hh;
        return;
      }
      if (lst.n !== q) {
        var why;
        if (lst.n % L.PACK) {
          var dn = lst.n - lst.n % L.PACK, up = dn + L.PACK;
          why = 'Seals come in sets of 12, so the sheet needs ' + (dn >= api.minSeals() ? fmt(dn) + ' or ' : '') +
                fmt(up) + ' rows.';
        } else if (lst.n < api.minSeals()) {
          why = 'The smallest order is ' + fmt(api.minSeals()) + ' seals — add rows to reach it.';
        } else {
          why = 'Change the quantity to ' + fmt(lst.n) + ' seals, or fix the sheet.';
        }
        hh += '<div class="svql-msg warn"><b>Your sheet has ' + fmt(lst.n) + ' rows but you’re ordering ' +
              fmt(q) + ' seals.</b> Every seal needs exactly one row. ' + why +
              (lst.n % L.PACK === 0 && lst.n >= api.minSeals() && lst.n <= Math.min(api.maxSeals(), L.MAX_ROWS)
                ? ' <button type="button" class="svql-link" data-svql="setq">Order ' + fmt(lst.n) + ' seals</button>' : '') +
              '</div>';
      } else {
        hh += '<div class="svql-msg good"><b>' + fmt(lst.n) + ' serials and links checked</b> from ' + esc(lst.name) +
              '. Tap a row to see it on the seal, then scan the preview with your phone.' +
              '<div class="svql-fee">Per-seal links: ' + fmt(lst.n) + ' × $0.10 = <b>' + money(lst.n * L.PRICE) +
              '</b>, added as its own cart line.</div></div>';
      }
      hh += '<div class="svql-tbl" role="listbox" aria-label="Your serials and links">' +
            '<div class="svql-tr hd" aria-hidden="true"><span class="n">#</span><span>Serial</span><span class="l">Link</span></div>' +
            lst.rows.map(function (r, i) {
              return '<div class="svql-tr' + (i === lst.pick ? ' on' : '') + '" role="option" tabindex="0" ' +
                'aria-selected="' + (i === lst.pick) + '" data-i="' + i + '"><span class="n">' + (i + 1) + '</span>' +
                '<span>' + esc(r.serial) + '</span><span class="l">' + esc(r.link.replace(/^https:\/\//, '')) + '</span></div>';
            }).join('') + '</div>';
      hh += '<label class="svql-ack"><input type="checkbox" data-svql="ack"' + (lst.ack ? ' checked' : '') + '>' +
            '<span>I checked every serial and link. They print exactly as listed, and a printed QR code ' +
            'can’t be changed afterwards.</span></label>';
      out.innerHTML = hh;
      var sq = out.querySelector('[data-svql="setq"]');
      if (sq) sq.onclick = function () { api.setQty(lst.n); paint(); };
      out.querySelectorAll('.svql-tr[data-i]').forEach(function (tr) {
        var go = function () {
          lst.pick = parseInt(tr.getAttribute('data-i'), 10) || 0;
          out.querySelectorAll('.svql-tr.on').forEach(function (x) { x.classList.remove('on'); x.setAttribute('aria-selected', 'false'); });
          tr.classList.add('on');
          tr.setAttribute('aria-selected', 'true');
          api.pick(lst.pick);
        };
        tr.onclick = go;
        tr.onkeydown = function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } };
      });
      var ack = out.querySelector('[data-svql="ack"]');
      if (ack) ack.onchange = function () { lst.ack = !!this.checked; api.set(lst); };
    }
    paint();
    return { paint: paint };
  }

  window.SVQL = {
    LIMITS: L, clean: clean, checkSerial: checkSerial, checkLink: checkLink, normLink: normLink,
    parseDelimited: parseDelimited, check: check, readFile: readFile, readText: readText,
    fromStored: fromStored, sha16: sha16, templateXlsx: templateXlsx, templateCsv: templateCsv,
    panel: panel, clearDraft: clearDraft, wantsList: wantsList,
    /* PUBLIC: false until Label Farm can print these lines (theme deploys
       LAST - serialseal-pos-item-code release lesson). ?qrlist=1 opens it
       for this browser session for testing. */
    PUBLIC: true
  };
})();
